import {
  ChangeEventHandler,
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { useGetWasteItemsQuery } from '../../../../api/otherApi';
import CheckBoxGroup, {
  CheckBoxGroupOnChangeProps,
  ValueObj,
} from '../../../../components/common/CheckBoxGroup';
import {
  CalcStatusRequest,
  GetContractCondition,
} from '../../../../types/contracts';
import styles from './styles.module.css';
import Input from '../../../../components/common/Input';
import Button from '../../../../components/common/Button';
import DateInput from '../../../../components/common/DateInput';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp } from '@fortawesome/free-solid-svg-icons';
import Select, { Option } from '../../../../components/common/Select';
import { format } from 'date-fns';
import { keyList, useKeyAction } from '../../../../services/useKeyAction';

const workCategoryOptions: Option[] = [
  { value: '0', label: '積保' },
  { value: '1', label: '直行' },
];
const statusOptions: Option[] = [
  { label: '未配車', value: 'uncalced' },
  { label: '計算済', value: 'completed' },
  { label: '配車不可', value: 'incomplete' },
  { label: 'すべて', value: 'all' },
] satisfies Array<{ label: string; value: CalcStatusRequest }>;

type Props = {
  conditions: GetContractCondition;
  initConditions: GetContractCondition;
  updateConditions: Dispatch<SetStateAction<GetContractCondition>>;
};
const Conditions: FC<Props> = ({
  conditions,
  initConditions,
  updateConditions,
}) => {
  const [goesStraight, setGoesStraight] = useState(
    conditions.goesStraight !== undefined ? conditions.goesStraight : null
  );
  const onChangeGoesStraight: ChangeEventHandler<HTMLSelectElement> = (e) => {
    const value = e.target.value;
    setGoesStraight(value ? Number(value) : null);
  };
  // useEffect のトリガ条件に関わるためデフォルト値を指定しない
  const { data: wasteItems } = useGetWasteItemsQuery();
  const [wasteItemCheckBoxGroupValues, setWasteItemCheckBoxGroupValues] =
    useState<ValueObj[]>([]);
  useEffect(() => {
    setWasteItemCheckBoxGroupValues(
      (wasteItems || []).map((item, i) => ({
        value: `${item.wasteItemId}`,
        label: `${i + 1}.${item.wasteItem}`,
        checked: false,
      }))
    );
  }, [wasteItems]);
  const [wasteItemIds, setWasteItemIds] = useState<number[]>([]);
  const onChangeWasteItem = ({
    values,
    selectedValues,
  }: CheckBoxGroupOnChangeProps) => {
    setWasteItemCheckBoxGroupValues(values);
    setWasteItemIds(selectedValues.map((v) => Number(v)));
  };
  const [contractNo, setContractNo] = useState(
    conditions.contractNo !== undefined ? conditions.contractNo : null
  );

  const [calcStatus, setCalcStatus] = useState<CalcStatusRequest>(
    conditions.calcStatus
  );
  const [startDate, setStartDate] = useState(conditions.startDate || '');
  const [endDate, setEndDate] = useState(conditions.endDate || '');
  const [companyName, setCompanyName] = useState(conditions.companyName || '');
  const [isOpened, setIsOpened] = useState(false);
  const onClear = () => {
    setGoesStraight(
      initConditions.goesStraight !== undefined
        ? initConditions.goesStraight
        : null
    );
    setWasteItemCheckBoxGroupValues(
      wasteItemCheckBoxGroupValues.map((item) => ({
        ...item,
        checked: false,
      }))
    );
    setWasteItemIds([]);
    setContractNo(
      initConditions.contractNo !== undefined ? initConditions.contractNo : null
    );
    setCalcStatus(initConditions.calcStatus);
    setStartDate('');
    setEndDate(initConditions.endDate || '');
    setCompanyName(initConditions.companyName || '');
    updateConditions((prev) => ({
      ...prev,
      contractNo: null,
      startDate: '',
      endDate: '',
      companyName: '',
      calcStatus: 'uncalced',
      goesStraight: null,
      wasteItemIds: [],
      page: 1,
    }));
  };
  const onSubmit = () => {
    updateConditions((prev) => ({
      ...prev,
      contractNo: contractNo,
      goesStraight: goesStraight,
      wasteItemIds: wasteItemIds,
      calcStatus,
      startDate,
      endDate,
      companyName: companyName,
      page: 1,
    }));
  };

  const onConditionEnter = useKeyAction(keyList.ENTER, onSubmit);

  return (
    <>
      <div className={styles.container} onKeyDown={onConditionEnter}>
        <div className={styles.textConditions}>
          <div className={styles.textConditionsLine}>
            <div className={styles.contractNo}>
              <Input
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={15}
                placeholder="契約No"
                value={contractNo !== null ? `${contractNo}` : ''}
                onChange={(e) => {
                  const s = e.target.value
                    .trim()
                    .replace(/[０-９]/g, (s) =>
                      String.fromCharCode(s.charCodeAt(0) - 0xfee0)
                    );
                  if (s === '') {
                    setContractNo(null);
                  } else {
                    const n = Number(s);
                    if (!Number.isNaN(n)) setContractNo(n);
                  }
                }}
                variants={['fit']}
              />
            </div>
            <div className={styles.dates}>
              <DateInput
                placeholder="開始日"
                value={startDate ? new Date(startDate) : undefined}
                onChange={(day) => {
                  setStartDate(day ? format(day, 'yyyy-MM-dd') : '');
                  if (day && day > new Date(endDate))
                    setEndDate(format(day, 'yyyy-MM-dd'));
                }}
              />
              <span className={styles.fromTo}>~</span>
              <DateInput
                placeholder="終了日"
                value={endDate ? new Date(endDate) : undefined}
                onChange={(day) => {
                  setEndDate(day ? format(day, 'yyyy-MM-dd') : '');
                  if (day && day < new Date(startDate))
                    setStartDate(format(day, 'yyyy-MM-dd'));
                }}
              />
            </div>
            <div className={styles.companyName}>
              <Input
                placeholder="排出事業者・委託先・排出事業場"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                variants={['fit']}
              />
            </div>
          </div>
          {isOpened && (
            <div className={styles.textConditionsLine}>
              <div className={styles.workCategoryId}>
                <Select
                  options={workCategoryOptions}
                  value={
                    goesStraight !== undefined && goesStraight !== null
                      ? `${goesStraight}`
                      : ''
                  }
                  onChange={onChangeGoesStraight}
                  placeholder="作業内容"
                  variants={['fit']}
                ></Select>
              </div>
              <div className={styles.wasteItem}>
                <CheckBoxGroup
                  values={wasteItemCheckBoxGroupValues}
                  onChange={onChangeWasteItem}
                  placeholder="廃棄品目"
                />
              </div>
            </div>
          )}
          <div>
            <button
              className={styles.conditionsOpener}
              onClick={() => setIsOpened(!isOpened)}
            >
              <FontAwesomeIcon
                icon={isOpened ? faAngleUp : faAngleDown}
                className={styles.arrow}
              />
              {isOpened ? 'その他の検索条件を非表示' : 'その他の検索条件を表示'}
            </button>
          </div>
        </div>
        <div className={styles.radioConditions}>
          <Select
            options={statusOptions}
            variants={['fit']}
            value={calcStatus}
            onChange={(e) => setCalcStatus(e.target.value as CalcStatusRequest)}
          />
        </div>
        <div className={styles.conditionButtons}>
          <div className={styles.clearButton}>
            <Button variants={['contain', 'cancel']} onClick={onClear}>
              クリア
            </Button>
          </div>
          <Button
            variants={['contain', 'secondary', 'search']}
            onClick={onSubmit}
          >
            検索
          </Button>
        </div>
      </div>
    </>
  );
};
export default Conditions;
